/**
 * useTutorLiveStudents — la pellicule de la salle du masteur : les élèves, leur vidéo et leur puce, Dialogue /
 * Concentré, la main d'un élève, la liste de l'élève regardé, son objectif de séance, sa fiche.
 *
 * Ce que chaque tuile de l'ancien live faisait seule (`common/liveMode/liveV3/LiveParticipant.js`,
 * `LiveParticipantIndicator.js`, `TutorFocusLayoutM.js`, `tutor/students/NewStudentsListItem.js`), en un seul
 * crochet, **avec le même protocole** (tech-derisk, point 7) : chaque geste part avec le même service, les mêmes
 * champs, et le même message de salle — la table `TUTOR_LIVE_STUDENTS_EQUIVALENCE`, tenue par un test par ligne.
 *
 * Ce qu'il lit : la séance (`currentUser.live_sessions`, par `selectAccount`), la tranche `liveMode` qu'écrit
 * l'écouteur global `LKEvents` (présences, mains, modes, champion, photo, hors appli, connexions, activités vues,
 * métriques), et du moteur commun les pistes (caméra, micro). Qui parle ne passe pas par lui : chaque vignette le lit
 * seule (`LiveSpeaking`), sans redessiner la salle. Ce qu'il lit lui-même au serveur, comme
 * l'ancien widget du prochain contrôle : les contrôles à venir de l'élève regardé (les métriques, que lisait
 * l'ancien `LiveMainLayout`, sont relues par la salle, `useTutorLive`).
 *
 * Ses échecs ne passent pas par la boîte réseau globale (`apiCall`, comme le live de l'élève) : une bascule
 * refusée se dit en snackbar (l'ancien `useApiStatus` de `TutorLiveScreen.js:147-152` ouvrait une boîte).
 *
 * Il rend sa zone (`TutorLiveStudentsZone`), la fiche ouverte (`profile`), ce qu'il pose par-dessus la salle
 * (`layer` : « Séance individuelle », « interrompre le point d'avancement », la snackbar d'un échec), les élèves
 * (que le centre et le fil lisent aussi) et `lowerHand`, que la carte « main levée » du fil appelle (S-116).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useMemo, useReducer } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setShowRaiseHandCheckpoint } from "../../../../../../data/redux/actions/liveMode"
import { setTodoActivities } from "../../../../../../data/redux/actions/data"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { liveService } from "../../../../../../data/services/liveService"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { studentBriefService } from "../../../../../../data/services/studentBriefService"
import { studentsService } from "../../../../../../data/services/studentsService"
import { apiCall, type ApiService } from "../../../../../../engine/shared/apiCall"
import {
    type TutorLiveProfileZone,
    type TutorLiveStudentsZone,
} from "../../../../../../design-system/pages/TutorLivePage/TutorLivePage"
import { indexSubjectsById, type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import { briefObjectiveId } from "../../../../adapters/tutor/Live/sessionObjectiveAdapter"
import {
    closestNextOccurrence,
    displayNameOf,
    isHolidayRoom,
    lastConversationId,
    type ApiActivityTemplate,
    type ApiLiveStudent,
    type ApiTutorLiveSession,
} from "../../../../adapters/tutor/Live/tutorLiveStudentAdapter"
import {
    toTutorLiveStudentProfile,
    type ApiProfileStudent,
    type ApiStudentBrief,
} from "../../../../adapters/tutor/Live/tutorLiveStudentProfileAdapter"
import { selectAccount, selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"
import {
    allStudentsFocused,
    defaultSelectedId,
    isIndividualSession,
    modePendingIdOf,
    sessionStudents,
    soundModeOf,
    tutorLiveStudentsViewModel,
    type LiveModeReading,
} from "../../../../view-models/tutor/Live/tutorLiveStudentsViewModel"
import { helperConversationIds, OUT_OF_APP_DEBOUNCE_MS } from "../../../../view-models/tutor/Live/tutorLiveFeedRules"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { type UISoundMode } from "../../../types/studentLive"
import { type UITutorStudentTab } from "../../../types/tutorEleves"
import { type UITutorLiveStudent } from "../../../types/tutorLive"
import { rawId, type TutorLiveLayer, type TutorLiveRoomContext, type TutorLiveSelection } from "./tutorLiveContract"

// ── La table d'équivalence ──────────────────────────────────────────────────────────────────────

/** Un appel d'API d'un geste : le service, et la forme de ses paramètres. */
export type EquivalenceCall = { service: string; routeParams?: string; data?: string; urlParams?: string }

/** Une ligne : le geste, d'où il vient dans le legacy, ses appels, son message de salle, sa mesure. */
export type EquivalenceRow = {
    gesture: string
    legacy: string
    calls: EquivalenceCall[]
    /** Le message de salle (`useLiveKitEventSender`) : son type, ses champs, ses destinataires. */
    message: { type: string; fields: string; to: string } | null
    /** La mesure Amplitude, sous son nom du legacy. */
    track: string | null
}

/**
 * Geste → service → message de salle. Chaque ligne a son test (`useTutorLiveStudents.equivalence.hook.test.tsx`) :
 * le geste joué sur le crochet part avec ces appels, ces champs, ce message.
 */
export const TUTOR_LIVE_STUDENTS_EQUIVALENCE = {
    sound_mode: {
        gesture: "Basculer un élève en Dialogue ou en Concentré (seul cet élève change)",
        legacy: "common/liveMode/liveV3/LiveParticipant.js:118-137",
        calls: [
            { service: "setSoundModeV2", routeParams: "{ id: séance }", data: "{ enabled, user_ids: [élève] }" },
            { service: "lowerHandsV2", routeParams: "{ id: séance }", data: "{ user_ids: [élève] }" },
        ],
        message: null,
        track: null,
    },
    interrupt_accept: {
        gesture: "« Oui, interrompre » : l'élève est en point d'avancement avec le champion, puis la bascule",
        legacy: "common/liveMode/liveV3/LiveParticipant.js:139-166",
        calls: [
            {
                service: "finishConversation",
                routeParams: "{ conversation_id: sa dernière }",
                data: '{ interruption_reason: "tutor_interruption", ended_at }',
            },
            { service: "setSoundModeV2", routeParams: "{ id: séance }", data: "{ enabled: true, user_ids: [élève] }" },
            { service: "lowerHandsV2", routeParams: "{ id: séance }", data: "{ user_ids: [élève] }" },
        ],
        message: null,
        track: "checkpoint.interrupt.accept",
    },
    interrupt_reject: {
        gesture: "« Non » : il reste avec le champion, rien ne part",
        legacy: "common/liveMode/liveV3/LiveParticipant.js:168-171",
        calls: [],
        message: null,
        track: "checkpoint.interrupt.reject",
    },
    individual_sound: {
        gesture: "Séance individuelle : toucher le son explique, sans rien basculer",
        legacy: "common/liveMode/liveV3/LiveParticipant.js:92-106",
        calls: [],
        message: null,
        track: null,
    },
    lower_hand: {
        gesture: "Baisser la main d'un élève (l'« OK » de la pastille, la coche de la carte du fil)",
        legacy: "common/liveMode/liveV3/LiveParticipantIndicator.js:199-218",
        calls: [{ service: "lowerHandsV2", routeParams: "{ id: séance }", data: "{ user_ids: [élève] }" }],
        message: null,
        track: null,
    },
    reorder_todo: {
        gesture: "Réordonner « À faire » de l'élève regardé",
        legacy: "tutor/students/NewStudentsListItem.js:331-347,764-799",
        calls: [
            { service: "activityTodoIndexesAsTutor", routeParams: "{ id: élève }", data: "{ todo_activities_ids }" },
        ],
        message: { type: "newTodo", fields: "{ id: élève }", to: "[élève]" },
        track: null,
    },
    archived_todo: {
        gesture:
            "Un archivage réussi : la pellicule n'envoie rien, le centre envoie le seul `deleteTodo` (useTutorLiveCenterActions)",
        legacy: "tutor/students/NewStudentsListItem.js:349-366",
        calls: [],
        message: null,
        track: null,
    },
    open_profile: {
        gesture: "Ouvrir la fiche de l'élève par-dessus la salle",
        legacy: "tutor/students/StudentProfil.js:93-118, NewStudentsListItem.js:1548-1575",
        calls: [
            { service: "getStudent", routeParams: "{ id: élève }" },
            {
                service: "getStudentBrief",
                routeParams: "{ id: élève }",
                urlParams: "{ objective_id, live_occurrence_id }",
            },
        ],
        message: null,
        track: "student_brief.opened",
    },
    next_assessment: {
        gesture: "Le prochain contrôle de l'élève regardé (hors stage)",
        legacy: "student/schoolAssessments/NextAssessmentWidget.js:53-63",
        calls: [{ service: "getSchoolAssessments", urlParams: "{ student_id, upcoming: true }" }],
        message: null,
        track: null,
    },
} satisfies Record<string, EquivalenceRow>

// ── L'état local ────────────────────────────────────────────────────────────────────────────────

type PendingDialog = { kind: "individual" } | { kind: "interrupt"; studentId: string; mode: UISoundMode }

export type TutorLiveStudentsState = {
    dialog: PendingDialog | null
    snackbar: string | null
    expandedDone: string[]
    outOfAppSince: Record<string, number>
    now: number
    profileTab: UITutorStudentTab
    brief: { studentId: string; data: ApiStudentBrief | null; loading: boolean } | null
    assessmentsAskedFor: string[]
}

export type TutorLiveStudentsEvent =
    | { type: "dialog"; dialog: PendingDialog | null }
    | { type: "snackbar"; message: string | null }
    | { type: "expand_done"; studentId: string }
    | { type: "out_of_app"; ids: string[]; at: number }
    | { type: "tick"; now: number }
    | { type: "profile_tab"; tab: UITutorStudentTab }
    | { type: "brief"; brief: TutorLiveStudentsState["brief"] }
    | { type: "assessments_asked"; studentId: string }

export function initialTutorLiveStudentsState(now: number): TutorLiveStudentsState {
    return {
        dialog: null,
        snackbar: null,
        expandedDone: [],
        outOfAppSince: {},
        now,
        profileTab: "overview",
        brief: null,
        assessmentsAskedFor: [],
    }
}

/**
 * Le réducteur pur de l'état local. « Hors appli » : chaque élève garde l'heure où il est sorti, tant qu'il
 * reste dehors ; revenu, il quitte la liste aussitôt (l'anti-rebond de `LiveParticipantIndicator.js:56-77`).
 */
export function tutorLiveStudentsReducer(
    state: TutorLiveStudentsState,
    event: TutorLiveStudentsEvent
): TutorLiveStudentsState {
    switch (event.type) {
        case "dialog":
            return { ...state, dialog: event.dialog }
        case "snackbar":
            return { ...state, snackbar: event.message }
        case "expand_done":
            return state.expandedDone.includes(event.studentId)
                ? state
                : { ...state, expandedDone: [...state.expandedDone, event.studentId] }
        case "out_of_app": {
            const next: Record<string, number> = {}
            for (const id of event.ids) next[id] = state.outOfAppSince[id] ?? event.at
            return { ...state, outOfAppSince: next }
        }
        case "tick":
            return { ...state, now: event.now }
        case "profile_tab":
            return { ...state, profileTab: event.tab }
        case "brief":
            return { ...state, brief: event.brief }
        case "assessments_asked":
            return state.assessmentsAskedFor.includes(event.studentId)
                ? state
                : { ...state, assessmentsAskedFor: [...state.assessmentsAskedFor, event.studentId] }
    }
}

// ── Le crochet ──────────────────────────────────────────────────────────────────────────────────

/** Ce que les autres zones fournissent : le fil (ajouter, le compte « À traiter »), le chat, le centre. */
export type TutorLiveStudentsOptions = {
    /** « Ajouter une activité » (le ⋮ du centre, l'onglet Activités du téléphone) : la génération du fil. */
    onAddActivity?: (studentId: string) => void
    /** Les non-lus d'un élève, sur sa vidéo : son fil du chat. Défaut : `selection.openChat(élève)`. */
    onOpenChat?: (studentId: string) => void
    /** Son prochain contrôle : « Contrôles » au centre. */
    onOpenAssessments?: (studentId: string) => void
    /** Ce qui attend une décision du masteur, par élève (le bac « À traiter » du fil). */
    pendingOf?: Record<string, number> | null
    /** Les élèves dont la main levée attend « Oui, vas-y » ou « Non » (le fil). */
    handChoiceOf?: readonly string[] | null
    /** Répondre à cette main depuis sa carte : « Oui, vas-y » ou « Non » (le fil). */
    onHandChoice?: (studentId: string, choice: "send_sam" | "decline") => void
}

export type TutorLiveStudents = {
    zone: TutorLiveStudentsZone
    profile: TutorLiveProfileZone | null
    layer: TutorLiveLayer
    students: UITutorLiveStudent[]
    /** Baisser la main de cet élève seul (S-116) : la carte « main levée » du fil. */
    lowerHand: (studentId: string) => void
}

const TICK_MS = 10 * 1000
const INTERRUPT_PRIORITY = 50
const INDIVIDUAL_PRIORITY = 40

type ApiSlice = {
    request?: boolean
    success?: boolean
    data?: unknown
    routeParams?: Record<string, unknown>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReduxState = any

const EMPTY: never[] = []
const selectLiveMode = (state: ReduxState): LiveModeReading => state?.liveMode ?? {}
const selectSubjects = (state: ReduxState): ApiSchoolSubject[] | undefined => state?.data?.staticData?.school_subjects
const selectTemplates = (state: ReduxState): ApiActivityTemplate[] | undefined =>
    state?.data?.staticData?.activity_templates
const selectSpecialNeeds = (state: ReduxState): Record<string, string> | undefined =>
    state?.data?.staticData?.student_special_needs
const selectAssessments = (state: ReduxState) => state?.data?.schoolAssessments?.byStudentId
const selectSoundModeSlice = (state: ReduxState): ApiSlice | undefined => state?.api?.setSoundModeV2

export function useTutorLiveStudents(
    room: TutorLiveRoomContext,
    selection: TutorLiveSelection,
    options: TutorLiveStudentsOptions = {}
): TutorLiveStudents {
    const dispatch = useDispatch()
    const { liveSessionId, live, telemetry } = room
    const [state, dispatchUi] = useReducer(tutorLiveStudentsReducer, Date.now(), initialTutorLiveStudentsState)

    const account = useSelector(selectAccount)
    const liveMode = useSelector(selectLiveMode)
    const subjects = useSelector(selectSubjects)
    const templates = useSelector(selectTemplates)
    const specialNeedLabels = useSelector(selectSpecialNeeds)
    const assessmentsByStudentId = useSelector(selectAssessments)
    const soundModeSlice = useSelector(selectSoundModeSlice)
    const profileStudentFromStore = useSelector((reduxState: ReduxState) =>
        selection.profileId != null ? selectDisplayedStudent(reduxState, selection.profileId) : null
    ) as ApiProfileStudent | null

    const meId = account?.id != null ? String(account.id) : null
    const session = useMemo(
        () =>
            ((account?.live_sessions ?? []) as ApiTutorLiveSession[]).find(
                (candidate) => candidate?.id != null && String(candidate.id) === String(liveSessionId)
            ) ?? null,
        [account?.live_sessions, liveSessionId]
    )
    const subjectsById = useMemo(() => indexSubjectsById(subjects), [subjects])
    const rawStudents = useMemo(() => sessionStudents(session, meId), [session, meId])
    const rawOf = useCallback(
        (studentId: string): ApiLiveStudent | undefined =>
            rawStudents.find((student) => String(student.id) === studentId),
        [rawStudents]
    )

    // Les lectures du serveur que le crochet lance : un échec est un fil d'Ariane, comme l'ancien les taisait.
    const read = useCallback(
        <T>(service: ApiService, params: Parameters<typeof apiCall>[2]) =>
            apiCall<T>(dispatch as never, service, params).catch((cause: unknown) => {
                telemetry.transition("reading", "failed", service.name, {
                    status: (cause as { status?: number | null })?.status ?? null,
                })
                return null
            }),
        [dispatch, telemetry]
    )

    /* ── l'heure, qui fait avancer les chronos ── */
    useEffect(() => {
        const timer = setInterval(() => dispatchUi({ type: "tick", now: Date.now() }), TICK_MS)
        return () => clearInterval(timer)
    }, [])

    /* ── hors de l'appli : l'heure de sortie, puis un réveil 3 s plus tard ── */
    const outOfAppKey = ((liveMode as { outOfAppIds?: (string | number)[] }).outOfAppIds ?? EMPTY)
        .map(String)
        .sort()
        .join(",")
    useEffect(() => {
        const ids = outOfAppKey === "" ? [] : outOfAppKey.split(",")
        dispatchUi({ type: "out_of_app", ids, at: Date.now() })
        if (ids.length === 0) return
        const timer = setTimeout(() => dispatchUi({ type: "tick", now: Date.now() }), OUT_OF_APP_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [outOfAppKey])

    // Les métriques des chronos (`getMetricsV2`) sont relues par la salle (`useTutorLive`), une seule fois.

    /* ── les élèves ── */
    const students = useMemo(
        () =>
            tutorLiveStudentsViewModel({
                session,
                meId,
                liveMode,
                participants: live?.participants ?? EMPTY,
                outOfAppSince: state.outOfAppSince,
                subjectsById,
                templates,
                assessmentsByStudentId,
                expandedDone: state.expandedDone,
                pendingOf: options.pendingOf,
                handChoiceOf: options.handChoiceOf,
                now: state.now,
            }),
        [
            session,
            meId,
            liveMode,
            live?.participants,
            state.outOfAppSince,
            subjectsById,
            templates,
            assessmentsByStudentId,
            state.expandedDone,
            options.pendingOf,
            options.handChoiceOf,
            state.now,
        ]
    )

    /* ── l'élève regardé par défaut : le premier inscrit (S-80) ── */
    const firstId = defaultSelectedId(students)
    const { selectedId, select } = selection
    useEffect(() => {
        if (selectedId == null && firstId != null) select(firstId)
    }, [selectedId, firstId, select])

    /* ── le prochain contrôle de l'élève regardé, lu une fois par élève, hors stage (F-18) ── */
    const holiday = isHolidayRoom(session?.live_room)
    useEffect(() => {
        if (selectedId == null || holiday || state.assessmentsAskedFor.includes(selectedId)) return
        dispatchUi({ type: "assessments_asked", studentId: selectedId })
        void read(schoolAssessmentsService.getSchoolAssessments, {
            urlParams: { student_id: rawId(selectedId), upcoming: true },
        })
    }, [selectedId, holiday, state.assessmentsAskedFor, read])

    /*
     * ── un archivage réussi : rien ici ──
     * L'ancienne fiche (`NewStudentsListItem.js:349-366`) renvoyait `deleteTodo` sur tout succès de
     * `deleteActivitiesTodo`, en plus de celui de l'archivage (deux messages pour un geste, C-47). Dans la salle,
     * le seul archivage est celui du centre, qui envoie son `deleteTodo` lui-même (`useTutorLiveCenterActions`) :
     * la pellicule n'écoute pas la tranche, un archivage fait partir un message, un seul.
     */

    /* ── les gestes ── */
    const fail = useCallback(
        (error: unknown, fallback: string) =>
            dispatchUi({ type: "snackbar", message: apiErrorMessage(error, fallback) }),
        []
    )

    /** La bascule même (`applyToggle`, `LiveParticipant.js:123-137`) : le mode, puis la main, l'un après l'autre. */
    const applyMode = useCallback(
        (studentId: string, mode: UISoundMode) => {
            if (liveSessionId == null) return
            const userId = rawId(studentId)
            const name = displayNameOf(rawOf(studentId))
            apiCall(dispatch as never, liveServiceV2.setSoundModeV2, {
                routeParams: { id: liveSessionId },
                data: { enabled: mode === "dialogue", user_ids: [userId] },
            }).catch((error: unknown) =>
                fail(error, `Impossible de basculer ${name} en ${mode === "dialogue" ? "Dialogue" : "Concentré"}`)
            )
            void read(liveServiceV2.lowerHandsV2, { routeParams: { id: liveSessionId }, data: { user_ids: [userId] } })
        },
        [dispatch, liveSessionId, rawOf, read, fail]
    )

    const onChangeMode = useCallback(
        (studentId: string, mode: UISoundMode) => {
            // Séance individuelle : le son reste ouvert, toucher l'explique seulement (`LiveParticipant.js:92-106`).
            if (isIndividualSession(session)) {
                dispatchUi({ type: "dialog", dialog: { kind: "individual" } })
                return
            }
            const current = soundModeOf(session, liveMode, studentId)
            const toDialogue = current === "focused"
            // Tous en Concentré, j'en ouvre un : il devient l'élève regardé (`LiveParticipant.js:108-116`).
            if (toDialogue && allStudentsFocused(session, liveMode)) select(studentId)
            // Un point d'avancement parlé avec le champion s'interrompt sur demande ; le quiz de prérequis, lui, n'est
            // pas une conversation : l'élève entend le masteur et son quiz continue (Wajih, 05/11).
            const withHelper = helperConversationIds(
                liveMode.ongoingCheckpointIds,
                live?.participants ?? EMPTY
            ).includes(studentId)
            if (withHelper && toDialogue) {
                dispatch(setShowRaiseHandCheckpoint(rawId(studentId), false) as never)
                dispatchUi({ type: "dialog", dialog: { kind: "interrupt", studentId, mode } })
                return
            }
            applyMode(studentId, mode)
        },
        [session, liveMode, select, dispatch, applyMode, live?.participants]
    )

    const confirmInterrupt = useCallback(() => {
        const dialog = state.dialog
        dispatchUi({ type: "dialog", dialog: null })
        if (dialog?.kind !== "interrupt") return
        amplitude.track("checkpoint.interrupt.accept")
        const conversationId = lastConversationId(rawOf(dialog.studentId))
        if (conversationId != null) {
            void read(liveService.finishConversation, {
                routeParams: { conversation_id: conversationId },
                data: { interruption_reason: "tutor_interruption", ended_at: new Date().toISOString() },
            })
        }
        applyMode(dialog.studentId, dialog.mode)
    }, [state.dialog, rawOf, read, applyMode])

    const rejectInterrupt = useCallback(() => {
        dispatchUi({ type: "dialog", dialog: null })
        amplitude.track("checkpoint.interrupt.reject")
    }, [])

    const closeDialog = useCallback(() => dispatchUi({ type: "dialog", dialog: null }), [])

    const lowerHand = useCallback(
        (studentId: string) => {
            if (liveSessionId == null) return
            apiCall(dispatch as never, liveServiceV2.lowerHandsV2, {
                routeParams: { id: liveSessionId },
                data: { user_ids: [rawId(studentId)] },
            }).catch((error: unknown) =>
                fail(error, `Impossible de baisser la main de ${displayNameOf(rawOf(studentId))}`)
            )
        },
        [dispatch, liveSessionId, rawOf, fail]
    )

    /** Le glisser de « À faire » (`NewStudentsListItem.js:764-799`), puis `newTodo` à l'élève. */
    const onReorder = useCallback(
        (studentId: string, activityIds: string[]) => {
            const student = rawOf(studentId)
            const todos = (student?.todo_activities ?? []).filter((todo) => todo?.id != null)
            const reordered = activityIds
                .map((id) => todos.find((todo) => String(todo!.id) === id))
                .filter((todo) => todo != null)
            // L'ordre écrit tout de suite, comme l'ancien (`setTodoActivities`), avant la réponse.
            dispatch(setTodoActivities(reordered) as never)
            const userId = rawId(studentId) as number
            apiCall(dispatch as never, activitiesService.activityTodoIndexesAsTutor, {
                routeParams: { id: userId },
                data: { todo_activities_ids: reordered.map((todo) => todo!.id) },
            })
                .then(() => live?.send("newTodo", { id: userId }, [userId], "drop"))
                .catch((error: unknown) => fail(error, "Impossible d'enregistrer l'ordre des activités"))
        },
        [rawOf, dispatch, live, fail]
    )

    const onSeeMoreDone = useCallback((studentId: string) => dispatchUi({ type: "expand_done", studentId }), [])

    /** La fiche par-dessus la salle (`NewStudentsListItem.js:1548-1575`, `StudentProfil.js:93-118`). */
    const onOpenProfile = useCallback(
        (studentId: string) => {
            selection.openProfile(studentId)
            dispatchUi({ type: "profile_tab", tab: "overview" })
            const student = rawOf(studentId)
            const userId = rawId(studentId)
            const objectiveId = briefObjectiveId(student, session?.id)
            const occurrenceId = closestNextOccurrence(session)?.id ?? null
            void read(studentsService.getStudent, { routeParams: { id: userId } })
            const urlParams: Record<string, unknown> = {}
            if (objectiveId) urlParams.objective_id = objectiveId
            if (occurrenceId) urlParams.live_occurrence_id = occurrenceId
            dispatchUi({ type: "brief", brief: { studentId, data: null, loading: true } })
            void read<ApiStudentBrief>(studentBriefService.getStudentBrief, {
                routeParams: { id: userId },
                urlParams,
            }).then((data) => dispatchUi({ type: "brief", brief: { studentId, data, loading: false } }))
            amplitude.track("student_brief.opened", {
                context: "in_live",
                student_id: userId,
                has_objective: !!objectiveId,
            })
        },
        [selection, rawOf, session, read]
    )

    const onOpenChat = options.onOpenChat ?? ((studentId: string) => selection.openChat(studentId))

    const zone: TutorLiveStudentsZone = {
        list: students,
        selectedId: selection.selectedId,
        // Toucher un élève : le regarder et montrer son activité en cours au centre, à chaque toucher.
        onSelect: selection.look,
        onChangeMode,
        modePendingId: modePendingIdOf(soundModeSlice as Parameters<typeof modePendingIdOf>[0]),
        onOpenActivity: selection.openActivityFor,
        onAddActivity: options.onAddActivity ?? (() => {}),
        onOpenProfile,
        onReorder,
        onSeeMoreDone,
        onOpenChat,
        onOpenAssessments: options.onOpenAssessments,
        onHandChoice: options.onHandChoice,
    }

    /* ── la fiche ouverte ── */
    const profileId = selection.profileId
    const profileRaw = profileStudentFromStore ?? (profileId != null ? (rawOf(profileId) as ApiProfileStudent) : null)
    const brief = state.brief != null && state.brief.studentId === profileId ? state.brief : null
    const profileModel = useMemo(
        () =>
            profileId != null
                ? toTutorLiveStudentProfile(profileRaw, brief?.data, { subjectsById, specialNeedLabels })
                : null,
        [profileId, profileRaw, brief?.data, subjectsById, specialNeedLabels]
    )
    const profile: TutorLiveProfileZone | null =
        profileId != null
            ? {
                  fiche: {
                      profile: profileModel,
                      tab: state.profileTab,
                      onChangeTab: (tab: UITutorStudentTab) => dispatchUi({ type: "profile_tab", tab }),
                      briefLoading: brief?.loading === true,
                      loading: profileModel == null,
                  },
                  onBack: selection.closeProfile,
                  onClose: selection.closeProfile,
              }
            : null

    /*
     * ── ce qui se pose par-dessus la salle ──
     * Chaque surface suit un geste du masteur (toucher le son, baisser une main, réordonner) : aucune ne s'ouvre
     * seule, aucune ne se marque `dialogAutomatic` / `snackbarAutomatic` — elle reste montrée pendant « Présenter ».
     */
    const dialogStudent = state.dialog?.kind === "interrupt" ? rawOf(state.dialog.studentId) : undefined
    const layer: TutorLiveLayer = {
        dialog:
            state.dialog?.kind === "individual"
                ? { value: { kind: "individual_sound" }, onConfirm: closeDialog, onDismiss: closeDialog }
                : state.dialog?.kind === "interrupt"
                  ? {
                        value: {
                            kind: "interrupt_sam",
                            studentName: displayNameOf(dialogStudent),
                        },
                        onConfirm: confirmInterrupt,
                        onCancel: rejectInterrupt,
                    }
                  : null,
        dialogPriority: state.dialog?.kind === "interrupt" ? INTERRUPT_PRIORITY : INDIVIDUAL_PRIORITY,
        snackbar:
            state.snackbar != null
                ? {
                      value: { message: state.snackbar, tone: "danger" },
                      onDismiss: () => dispatchUi({ type: "snackbar", message: null }),
                  }
                : null,
    }

    return { zone, profile, layer, students, lowerHand }
}
